import React, { useState, useEffect, useRef, useCallback } from 'react';
import './ImageMap.scss';

// Type definitions for image map data structure
interface ImageMapArea {
  type: 'rectangle' | 'circle' | 'polygon';
  coords: {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    cx?: number;
    cy?: number;
    r?: number;
    points?: Array<{ x: number; y: number }>;
  };
  attributes: {
    id: string;
    title: string;
    href?: string;
    category: string;
  };
  processed_coords: string;
}

interface LegendItem {
  tag: string;
  category_ids: string;
}

interface ImageMapData {
  image_url: string;
  title?: string;
  description?: string;
  processed_areas: ImageMapArea[];
  legend_items: LegendItem[];
  has_areas: boolean;
}

interface ImageMapProps {
  event: {
    image_map_data?: ImageMapData;
  };
  onAreaClick?: (areaId: string, categoryId: string) => void;
  onAreaHover?: (areaId: string, categoryId: string) => void;
}

const ImageMap: React.FC<ImageMapProps> = ({ event, onAreaClick, onAreaHover }) => {
  const [highlightedAreas, setHighlightedAreas] = useState<Set<string>>(new Set());
  const [highlightedLegend, setHighlightedLegend] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  const imageRef = useRef<HTMLImageElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const imageMapData = event.image_map_data;
  const imageMapDataRef = useRef<ImageMapData | undefined>(imageMapData);

  // Update ref when data changes
  useEffect(() => {
    imageMapDataRef.current = imageMapData;
  }, [imageMapData]);

  // Handle image load
  const handleImageLoad = useCallback(() => {
    if (imageRef.current) {
      setImageLoaded(true);
    }
  }, []);

  // Update overlay positions based on image dimensions - matching old implementation
  const updateOverlayPositions = useCallback(() => {
    const currentImageMapData = imageMapDataRef.current;
    if (!imageRef.current || !overlayRef.current || !currentImageMapData?.processed_areas) {
      return;
    }

    const image = imageRef.current;
    if (!image.naturalWidth || !image.naturalHeight) {
      return;
    }

    const imageRect = image.getBoundingClientRect();
    const scaleX = imageRect.width / image.naturalWidth;
    const scaleY = imageRect.height / image.naturalHeight;

    // Update overlay positions based on current image dimensions

    // Update both area coordinates and overlay positions simultaneously
    currentImageMapData.processed_areas.forEach((area) => {
      const overlay = overlayRef.current?.querySelector(`.area-overlay[data-area-id="${area.attributes.id}"]`);
      const areaElement = document.querySelector(`area[data-area-id="${area.attributes.id}"]`);
      
      if (!overlay) return;

      const coords = area.processed_coords.split(',').map(Number);
      const shape = area.type === 'rectangle' ? 'rect' : (area.type === 'polygon' ? 'poly' : area.type);

      try {
        if (shape === 'rect' && coords.length >= 4) {
          const [x1, y1, x2, y2] = coords;
          const left = x1 * scaleX;
          const top = y1 * scaleY;
          const width = (x2 - x1) * scaleX;
          const height = (y2 - y1) * scaleY;

          // Update overlay position
          (overlay as HTMLElement).style.left = `${left}px`;
          (overlay as HTMLElement).style.top = `${top}px`;
          (overlay as HTMLElement).style.width = `${width}px`;
          (overlay as HTMLElement).style.height = `${height}px`;
          (overlay as HTMLElement).style.borderRadius = '0';
          (overlay as HTMLElement).style.clipPath = 'none';
          (overlay as HTMLElement).style.transform = 'none';

          // Update area element coordinates to match
          if (areaElement) {
            const scaledCoords = [
              Math.round(x1 * scaleX),
              Math.round(y1 * scaleY),
              Math.round(x2 * scaleX),
              Math.round(y2 * scaleY)
            ];
            areaElement.setAttribute('coords', scaledCoords.join(','));
          }
        } else if (shape === 'circle' && coords.length >= 3) {
          const [cx, cy, r] = coords;
          const scale = Math.min(scaleX, scaleY); // Use minimum scale to maintain circle shape
          const diameter = r * 2 * scale;

          // Update overlay position
          (overlay as HTMLElement).style.left = `${(cx - r) * scaleX}px`;
          (overlay as HTMLElement).style.top = `${(cy - r) * scaleY}px`;
          (overlay as HTMLElement).style.width = `${diameter}px`;
          (overlay as HTMLElement).style.height = `${diameter}px`;
          (overlay as HTMLElement).style.borderRadius = '50%';
          (overlay as HTMLElement).style.clipPath = 'none';
          (overlay as HTMLElement).style.transform = 'none';

          // Update area element coordinates to match
          if (areaElement) {
            const scaledCoords = [
              Math.round(cx * scaleX),
              Math.round(cy * scaleY),
              Math.round(r * scale)
            ];
            areaElement.setAttribute('coords', scaledCoords.join(','));
          }
        } else if (shape === 'poly' && coords.length >= 6) {
          const points = [];
          const scaledCoords = [];
          for (let i = 0; i < coords.length; i += 2) {
            const x = coords[i];
            const y = coords[i + 1];
            const xPercent = (x / image.naturalWidth) * 100;
            const yPercent = (y / image.naturalHeight) * 100;
            points.push(`${xPercent}% ${yPercent}%`);
            
            // Also calculate scaled coordinates for area element
            scaledCoords.push(Math.round(x * scaleX), Math.round(y * scaleY));
          }

          // Update overlay position
          (overlay as HTMLElement).style.left = '0';
          (overlay as HTMLElement).style.top = '0';
          (overlay as HTMLElement).style.width = '100%';
          (overlay as HTMLElement).style.height = '100%';
          (overlay as HTMLElement).style.borderRadius = '0';
          (overlay as HTMLElement).style.clipPath = `polygon(${points.join(',')})`;
          (overlay as HTMLElement).style.transform = 'none';

          // Update area element coordinates to match
          if (areaElement) {
            areaElement.setAttribute('coords', scaledCoords.join(','));
          }
        }

        overlay.classList.add('visible');
      } catch (error) {
        console.warn('Error updating overlay position:', error);
      }
    });
  }, []);

  // Handle area hover - matching old implementation
  const handleAreaHover = useCallback((areaId: string, categoryId: string) => {
    setHighlightedAreas(new Set([areaId]));
    onAreaHover?.(areaId, categoryId);
  }, [onAreaHover]);

  // Handle area mouse leave
  const handleAreaLeave = useCallback(() => {
    setHighlightedAreas(new Set());
  }, []);

  // Handle legend hover - matching old implementation
  const handleLegendHover = useCallback((tag: string) => {
    setHighlightedLegend(tag);
    const legendItem = imageMapData?.legend_items.find(item => item.tag === tag);
    if (legendItem) {
      const categoryIds = legendItem.category_ids.split(',');
      const areasToHighlight = imageMapData?.processed_areas
        .filter(area => categoryIds.includes(area.attributes.category))
        .map(area => area.attributes.id) || [];
      setHighlightedAreas(new Set(areasToHighlight));
    }
  }, [imageMapData]);

  // Handle legend leave
  const handleLegendLeave = useCallback(() => {
    setHighlightedLegend(null);
    setHighlightedAreas(new Set());
  }, []);

  // Handle area click
  const handleAreaClick = useCallback((areaId: string, categoryId: string) => {
    console.log('ImageMap: Area clicked', { areaId, categoryId });
    onAreaClick?.(areaId, categoryId);
  }, [onAreaClick]);

  // Handle window resize with debounce - matching old implementation
  const handleResize = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }
    resizeTimeoutRef.current = setTimeout(updateOverlayPositions, 150);
  }, [updateOverlayPositions]);

  // Handle orientation change - matching old implementation
  const handleOrientationChange = useCallback(() => {
    setTimeout(updateOverlayPositions, 100);
  }, [updateOverlayPositions]);

  // Set up event listeners
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [handleResize, handleOrientationChange]);

  // Update overlay positions when image loads
  useEffect(() => {
    if (imageLoaded) {
      updateOverlayPositions();
    }
  }, [imageLoaded, updateOverlayPositions]);

  // Update overlay positions when data changes
  useEffect(() => {
    if (imageLoaded && imageMapData?.processed_areas) {
      updateOverlayPositions();
    }
  }, [imageMapData, imageLoaded, updateOverlayPositions]);

  // Ensure area elements have correct coordinates when component mounts
  useEffect(() => {
    if (imageMapData?.processed_areas) {
      // Set initial coordinates for area elements
      imageMapData.processed_areas.forEach((area) => {
        const areaElement = document.querySelector(`area[data-area-id="${area.attributes.id}"]`);
        if (areaElement) {
          areaElement.setAttribute('coords', area.processed_coords);
          areaElement.setAttribute('data-original-coords', area.processed_coords);
        }
      });
    }
  }, [imageMapData]);

  // Create overlays when component mounts - matching old implementation
  useEffect(() => {
    if (imageMapData?.processed_areas && overlayRef.current) {
      // Clear existing overlays
      overlayRef.current.innerHTML = '';
      
      // Create overlays for each area
      imageMapData.processed_areas.forEach((area) => {
        const overlay = document.createElement('div');
        overlay.className = 'area-overlay';
        overlay.setAttribute('data-area-id', area.attributes.id);
        overlay.setAttribute('data-category-id', area.attributes.category);
        overlayRef.current?.appendChild(overlay);
      });
    }
  }, [imageMapData]);

  // Update overlay highlight classes when highlightedAreas changes
  useEffect(() => {
    if (!overlayRef.current) return;

    const overlays = overlayRef.current.querySelectorAll('.area-overlay');
    overlays.forEach((overlay) => {
      const areaId = overlay.getAttribute('data-area-id');
      if (areaId) {
        if (highlightedAreas.has(areaId)) {
          overlay.classList.add('highlight');
          overlay.classList.remove('visible');
        } else {
          overlay.classList.remove('highlight');
          overlay.classList.add('visible');
        }
      }
    });
  }, [highlightedAreas]);

  if (!imageMapData) {
    return null;
  }

  return (
    <div className="image-map-container">
      <h3 className="section-title">
        <i className="fas fa-map-marked-alt me-2"></i>
        {imageMapData.title || 'Seating Layout'}
      </h3>
      
      <div className="image-map-wrapper">
        <div className="image-map-viewer">
          <img
            ref={imageRef}
            src={imageMapData.image_url}
            alt="Event Layout"
            onLoad={handleImageLoad}
            className="layout-image"
            useMap="#event-map"
          />
          
          <div ref={overlayRef} className="area-overlays">
            {/* Overlays are created dynamically in useEffect */}
          </div>

          <map name="event-map" id="event-map">
            {imageMapData.processed_areas.map((area) => (
              <area
                key={area.attributes.id}
                shape={area.type === 'rectangle' ? 'rect' : (area.type === 'polygon' ? 'poly' : area.type)}
                coords={area.processed_coords}
                title={area.attributes.title}
                data-category-id={area.attributes.category}
                data-area-id={area.attributes.id}
                data-original-coords={area.processed_coords}
                id={area.attributes.id}
                onMouseOver={() => handleAreaHover(area.attributes.id, area.attributes.category)}
                onMouseOut={handleAreaLeave}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleAreaClick(area.attributes.id, area.attributes.category);
                }}
              />
            ))}
          </map>
        </div>
        
        {imageMapData.legend_items.length > 0 && (
          <div className="image-map-legend">
            <div className="legend d-flex flex-wrap">
              {imageMapData.legend_items.map((legendItem, index) => (
                <div
                  key={index}
                  className={`legend-item mb-2 me-2 ${highlightedLegend === legendItem.tag ? 'active' : ''}`}
                  data-tag={legendItem.tag}
                  data-category-ids={legendItem.category_ids}
                  onMouseOver={() => handleLegendHover(legendItem.tag)}
                  onMouseOut={handleLegendLeave}
                >
                  <span className="legend-color"></span>
                  <span className="legend-label">{legendItem.tag}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageMap;